import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, ButtonText } from '@/components/ui/button'
import { UpgradeModal, useUpsellBadge } from '@/components/feature-gate'

/**
 * The To-do card as an unentitled team sees it.
 *
 * The three preview rows are deliberately static copy, not real to-dos: a
 * blur is a visual effect, so real items would still be sitting in the page
 * source for anyone who opens devtools. They are illustrative of the *kinds*
 * of thing the list finds, which is what the upsell is actually selling.
 */

const PREVIEW_ROWS = [
  {
    glyph: '↑',
    tint: 'bg-[hsl(199,100%,93%)] text-[hsl(213,94%,35%)]',
    title: 'Confirm Sales — kitchens estimate',
    meta: '$42,000 expected 24 Sep',
  },
  {
    glyph: '!',
    tint: 'bg-[hsl(53,100%,89%)] text-[hsl(40,100%,30%)]',
    title: 'Contractors invoice unallocated',
    meta: '$3,180 · 4 days old',
  },
  {
    glyph: '↻',
    tint: 'bg-gray-low text-txt-secondary',
    title: 'BAS payment didn’t happen',
    meta: '$18,240 · due 28 Aug',
  },
]

export default function TodoLockedCard() {
  const navigate = useNavigate()
  // Badge from the API: while Pro is coming soon the card offers early access
  // (dialog) instead of the plans page.
  const comingSoon = useUpsellBadge('to-do') === 'coming_soon'
  const [earlyAccessOpen, setEarlyAccessOpen] = useState(false)
  const open = () =>
    comingSoon ? setEarlyAccessOpen(true) : navigate('/billing')

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            open()
          }
        }}
        className="flex w-full cursor-pointer flex-col gap-3 overflow-hidden rounded-[14px] border border-[hsla(261,92%,72%,.45)] bg-white p-4 text-left"
      >
        <div className="flex items-baseline justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-semibold text-txt-primary">
              To do
            </span>
            <span className="flex items-center gap-1.5 rounded-md border border-[hsla(261,92%,72%,.4)] bg-[hsl(285,100%,96%)] px-2 py-0.5 text-[11px] font-bold text-[hsl(261,56%,38%)]">
              <svg
                width="10"
                height="10"
                viewBox="0 0 12 12"
                fill="none"
                aria-hidden="true"
              >
                <rect
                  x="2.2"
                  y="5.2"
                  width="7.6"
                  height="5.4"
                  rx="1.4"
                  fill="currentColor"
                />
                <path
                  d="M4.1 5.2V3.9a1.9 1.9 0 0 1 3.8 0v1.3"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                />
              </svg>
              {comingSoon ? 'Coming soon' : 'Pro'}
            </span>
          </div>
        </div>

        <div className="relative flex flex-col gap-2">
          {PREVIEW_ROWS.map((row) => (
            <div
              key={row.title}
              className="flex items-center gap-2.5 rounded-[10px] border border-light px-3 py-2.5"
            >
              <span
                className={`flex size-6 shrink-0 items-center justify-center rounded-[7px] text-xs font-bold ${row.tint}`}
                aria-hidden="true"
              >
                {row.glyph}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-semibold text-txt-primary">
                  {row.title}
                </div>
                <div className="truncate text-[11.5px] text-txt-secondary">
                  {row.meta}
                </div>
              </div>
            </div>
          ))}
          <div
            aria-hidden="true"
            className="absolute inset-0 backdrop-blur-[2.5px]"
            style={{
              background:
                'linear-gradient(180deg, hsla(0,0%,100%,.5) 0%, hsla(0,0%,100%,.88) 50%, #fff 96%)',
            }}
          />
        </div>

        <div className="-mt-1 flex items-center gap-3 rounded-[11px] border border-[hsla(261,92%,72%,.4)] bg-[hsl(285,100%,97%)] p-3.5">
          <span className="flex size-[34px] shrink-0 items-center justify-center rounded-[10px] bg-[hsl(261,69%,59%)]">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <rect x="3" y="7" width="10" height="7" rx="1.8" fill="#fff" />
              <path
                d="M5.4 7V5.3a2.6 2.6 0 0 1 5.2 0V7"
                stroke="#fff"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[13.5px] font-semibold text-txt-primary">
              Unlock your to-do list
            </div>
            <div className="mt-0.5 text-xs leading-normal text-txt-secondary">
              Unallocated transactions, unconfirmed estimates and missed
              payments — found for you, ready to action.
            </div>
          </div>
          <Button size="sm" className="shrink-0">
            <ButtonText>
              {comingSoon ? 'Get early access' : 'See plans'}
            </ButtonText>
          </Button>
        </div>
      </div>
      <UpgradeModal
        feature={{ lookupKey: 'to-do', name: 'To-Do' }}
        open={earlyAccessOpen}
        onOpenChange={setEarlyAccessOpen}
      />
    </>
  )
}
